import { apiClient } from './api'

export type ServiceName = 'BLIS' | 'IZG'
export type PaymentGateway = 'stripe' | 'moyasar' | 'sadad'
export type SubscriptionStatus = 'pending' | 'active' | 'trial' | 'expired' | 'cancelled'

export interface Subscription {
  subscription_id: string
  organization_id: string
  service_name: ServiceName
  plan_tier: string
  plan_id?: string
  payment_gateway: PaymentGateway
  gateway_subscription_id?: string
  status: SubscriptionStatus
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
  created_at: string
  updated_at: string
}

export interface SubscriptionCreate {
  organization_id: string
  service_name: ServiceName
  plan_tier: string
  plan_id?: string
  payment_gateway: PaymentGateway
  gateway_subscription_id?: string
  billing_cycle: 'monthly' | 'annual'
}

export interface SubscriptionUpdate {
  plan_tier?: string
  plan_id?: string
  status?: SubscriptionStatus
}

export interface PlanInfo {
  plan_id: string
  plan_tier: string
  service_name: ServiceName
  name: string
  description: string
  price_monthly: number
  price_annual: number
  features: string[]
  usage_limits: Record<string, number>
}

export interface SubscriptionListResponse {
  subscriptions: Subscription[]
  total: number
}

export interface CancelSubscriptionRequest {
  cancel_at_period_end?: boolean
}

export const subscriptionService = {
  // List subscriptions
  listSubscriptions: async (params?: {
    organization_id?: string
    service_name?: ServiceName
  }): Promise<SubscriptionListResponse> => {
    const response = await apiClient.get<SubscriptionListResponse>(
      '/subscriptions',
      { params }
    )
    return response.data
  },

  // Get subscription details
  getSubscription: async (
    subscriptionId: string
  ): Promise<Subscription> => {
    const response = await apiClient.get<Subscription>(
      `/subscriptions/${subscriptionId}`
    )
    return response.data
  },

  // Create subscription
  createSubscription: async (
    data: SubscriptionCreate
  ): Promise<Subscription> => {
    const response = await apiClient.post<Subscription>('/subscriptions', data)
    return response.data
  },

  // Update subscription
  updateSubscription: async (
    subscriptionId: string,
    data: SubscriptionUpdate
  ): Promise<Subscription> => {
    const response = await apiClient.put<Subscription>(
      `/subscriptions/${subscriptionId}`,
      data
    )
    return response.data
  },

  // Cancel subscription
  cancelSubscription: async (
    subscriptionId: string,
    data?: CancelSubscriptionRequest
  ): Promise<Subscription> => {
    const response = await apiClient.post<Subscription>(
      `/subscriptions/${subscriptionId}/cancel`,
      data || { cancel_at_period_end: true }
    )
    return response.data
  },

  // Reactivate subscription
  reactivateSubscription: async (
    subscriptionId: string
  ): Promise<Subscription> => {
    const response = await apiClient.post<Subscription>(
      `/subscriptions/${subscriptionId}/reactivate`,
      {}
    )
    return response.data
  },

  // Get available plans
  getPlans: async (serviceName?: ServiceName): Promise<PlanInfo[]> => {
    const response = await apiClient.get<PlanInfo[]>('/subscriptions/plans', {
      params: serviceName ? { service_name: serviceName } : undefined,
    })
    return response.data
  },
}

